import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./index.scss";
import RealisationService from "../../services/realisation/realisation.service";
import { Container } from "react-bootstrap";
import Header from "../../components/appLayout/header";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

const realisationService = new RealisationService();

const iaPmeFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "L'IA va-t-elle remplacer mes employés ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Non. Elle automatise les tâches répétitives pour libérer votre équipe sur des missions à plus forte valeur. C'est un levier d'efficacité, pas un plan de licenciement.",
      },
    },
    {
      "@type": "Question",
      name: "Faut-il des compétences techniques pour utiliser l'IA ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Non. La grande majorité des outils sont conçus pour être pris en main sans aucune notion technique. Des formations existent si vous voulez aller plus loin.",
      },
    },
    {
      "@type": "Question",
      name: "Quelle IA choisir pour mon secteur ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Cela dépend de votre métier : un CRM intelligent pour le commerce, des outils de productivité pour les services, Pennylane ou Dext pour la compta, un ERP avec IA pour l'industrie.",
      },
    },
    {
      "@type": "Question",
      name: "Combien de temps dure un projet IA en PME ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Les premières automatisations se mettent en place en quelques jours à quelques semaines. Un déploiement complet prend généralement 2 à 6 mois.",
      },
    },
    {
      "@type": "Question",
      name: "L'IA générative est-elle adaptée aux TPE/PME ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, totalement. C'est même là qu'elle a le plus à offrir, et les fiches France Num/CNIL le confirment.",
      },
    },
  ],
};

export const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const apiUrl = process.env.REACT_APP_API_URL || "";

  const getImageURL = (imageName) =>
    imageName
      ? `${apiUrl.replace(/\/$/, "")}/blogs/${imageName}/image`
      : "/default-blog.png";

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await realisationService.getRealisationById(id);
        setBlog(res.data);
      } catch (err) {
        console.error("Erreur lors du chargement du blog :", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id]);

  useEffect(() => {
    if (!blog?.title?.includes("IA pour les PME")) {
      return undefined;
    }

    document.title = "IA pour les PME : par où commencer en 2026 | MSIT Conseil";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      "content",
      "IA pour les PME : le guide concret 2026 pour gagner du temps, choisir les bons outils et démarrer sans jargon ni risque RGPD. Les conseils de MSIT Conseil."
    );

    const existingScript = document.getElementById("ia-pme-faq-schema");
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ia-pme-faq-schema";
    script.text = JSON.stringify(iaPmeFaqSchema);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [blog]);

  if (loading) return <p>{t("blogDetails.loading")}</p>;
  if (!blog) return <p>{t("blogDetails.notFound")}</p>;

  // Extraire le texte du HTML pour la description
  const getTextFromHTML = (html) => {
    if (typeof window === "undefined") return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";
    return text.replace(/\s+/g, " ").trim();
  };

  const blogDescription = blog.description
    ? getTextFromHTML(blog.description).substring(0, 160) + "..."
    : "Article de blog MSIT Conseil sur la transformation digitale et les technologies.";
  const blogUrl = `https://www.msit-conseil.fr/blogDetails/${id}`;
  const blogImage = getImageURL(blog._id);

  return (
    <div className="blog-details-container mb-0">
      <SEO
        title={`${blog.title} - MSIT Conseil`}
        description={blogDescription}
        keywords={`${blog.title}, MSIT Conseil, transformation digitale, blog`}
        url={blogUrl}
        image={blogImage}
        type="article"
        article={{
          headline: blog.title,
          description: blogDescription,
          image: blogImage,
          datePublished: blog.date || blog.createdAt,
          dateModified: blog.updatedAt || blog.date || blog.createdAt,
          authorName: "MSIT Conseil",
        }}
        breadcrumbs={[
          { name: "Accueil", url: "https://www.msit-conseil.fr/" },
          { name: "Blog", url: "https://www.msit-conseil.fr/blogs" },
          { name: blog.title, url: blogUrl },
        ]}
      />
      <Header stylebg={"bg-color"} />
      <Container>
        <div className="blog-details-header mt-5">
          {t("blogDetails.article")}
        </div>
        <h1 className="blog-details-title mt-3">{blog.title}</h1>

        <div className="blog-details-image mt-5">
          <img
            className="img-fluid"
            src={getImageURL(blog._id)}
            alt={blog.title}
          />
        </div>

        <div
          className="blog-details-content mt-5"
          dangerouslySetInnerHTML={{ __html: blog.description }}
        />
      </Container>
    </div>
  );
};
